import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Media } from "../types/media";
import { DEFAULT_MEDIA_SORT, MediaSortBy } from "../types/mediaSort";
import { formatDistanceToNow } from "date-fns";
import ImageViewer from "./ImageViewer";
import AuthenticatedImage from "./AuthenticatedImage";
import MediaDaySectionHeader from "./MediaDaySectionHeader";
import { groupMediaByDay, mediaDayKey, shouldGroupMediaByDay } from "../utils/mediaDaySections";
import { albumDebugDuration } from "../utils/albumDebug";

const SKELETON_SLOT_COUNT = 12;
/** Fetch the next media page once this many skeleton tiles are approaching the viewport. */
const SKELETON_PREFETCH_AT = 4;

function skeletonPrefetchIndex(skeletonCount: number): number {
  if (skeletonCount <= 0) {
    return -1;
  }
  return Math.min(SKELETON_PREFETCH_AT, skeletonCount) - 1;
}

type GallerySlot = {
  id: string;
  index: number;
  isLoaded: boolean;
  data: Media | null;
};

interface SelectionToggleOptions {
  rangeSelect?: boolean;
  selected?: boolean;
}

interface MediaGalleryProps {
  media: Media[];
  totalMediaCount: number;
  onMediaClick?: (media: Media) => void;
  onDeleteMedia?: (media: Media) => void;
  isLoading?: boolean;
  isDeleting?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  showUploadButton?: boolean;
  onUploadClick?: () => void;
  onImageViewerStateChange?: (isOpen: boolean) => void;
  ownerId?: string;
  albumId?: string;
  selectionMode?: boolean;
  selectedMediaIds?: Set<string>;
  onSelectionModeChange?: (enabled: boolean) => void;
  onToggleSelection?: (mediaId: string, options?: SelectionToggleOptions) => void;
  onApplySelectionRange?: (
    anchorMediaId: string,
    currentMediaId: string,
    selected: boolean,
    baseSelectedMediaIds: Set<string>
  ) => void;
  isFiltered?: boolean;
  onFilteredClick?: () => void;
  sortBy?: MediaSortBy;
  loadedMediaCount?: number;
}

const MediaGallery: React.FC<MediaGalleryProps> = ({
  media,
  totalMediaCount,
  onMediaClick,
  // onDeleteMedia,
  isLoading = false,
  isDeleting = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage,
  showUploadButton = true,
  onUploadClick,
  onImageViewerStateChange,
  ownerId,
  albumId,
  selectionMode = false,
  selectedMediaIds,
  onSelectionModeChange,
  onToggleSelection,
  onApplySelectionRange,
  isFiltered = false,
  onFilteredClick,
  sortBy = DEFAULT_MEDIA_SORT.sortBy,
  loadedMediaCount,
}) => {
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [isTouchSelectionActive, setIsTouchSelectionActive] = useState(false);
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(() => new Set());
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const suppressSelectionClickUntilRef = useRef(0);
  const autoScrollFrameRef = useRef<number | null>(null);
  const latestTouchPointRef = useRef<{ x: number; y: number } | null>(null);
  const touchSelectionRef = useRef<{
    anchorMediaId: string;
    currentMediaId: string;
    selected: boolean;
    baseSelectedMediaIds: Set<string>;
    isActive: boolean;
    longPressTimerId: number | null;
    startX: number;
    startY: number;
  } | null>(null);

  // Index hydration can fill `media` with every album item. Only mount the fat-list
  // window plus a small skeleton row so opening a large album stays cheap.
  const mountedCount = Math.max(0, Math.min(loadedMediaCount ?? media.length, media.length));

  const loadedSlots = useMemo<Array<GallerySlot & { data: Media }>>(
    () =>
      media.slice(0, mountedCount).map((item, index) => ({
        id: item.id,
        index,
        isLoaded: true,
        data: item,
      })),
    [media, mountedCount],
  );

  const pendingSlots = useMemo<GallerySlot[]>(() => {
    const remaining = Math.max(totalMediaCount - mountedCount, 0);
    const wantsMore = Boolean(hasNextPage || isFetchingNextPage);
    if (!wantsMore) {
      return [];
    }
    const skeletonCount = Math.min(SKELETON_SLOT_COUNT, remaining || SKELETON_SLOT_COUNT);
    return Array.from({ length: skeletonCount }, (_, offset) => {
      const index = mountedCount + offset;
      return {
        id: `placeholder-${index}`,
        index,
        isLoaded: false,
        data: null,
      };
    });
  }, [hasNextPage, isFetchingNextPage, mountedCount, totalMediaCount]);

  const visibleSlots = useMemo<GallerySlot[]>(() => [...loadedSlots, ...pendingSlots], [loadedSlots, pendingSlots]);

  const prefetchSentinelIndex = skeletonPrefetchIndex(pendingSlots.length);
  const prefetchSentinelId = prefetchSentinelIndex >= 0 ? pendingSlots[prefetchSentinelIndex].id : null;

  const groupByDay = shouldGroupMediaByDay(sortBy);

  const daySections = useMemo(() => {
    if (!groupByDay) {
      return [];
    }

    const startedAt = performance.now();
    const indexById = new Map(media.map((item, index) => [item.id, index]));
    const sections = groupMediaByDay(media, (item) => mediaDayKey(item, sortBy));

    const consecutiveRuns: Array<{
      dayKey: string;
      count: number;
      firstId: string;
      firstFilename: string;
      firstTakenAt: string | null;
      firstContentType: string;
    }> = [];
    for (const item of media) {
      const dayKey = mediaDayKey(item, sortBy);
      const current = consecutiveRuns[consecutiveRuns.length - 1];
      if (current && current.dayKey === dayKey) {
        current.count += 1;
        continue;
      }
      consecutiveRuns.push({
        dayKey,
        count: 1,
        firstId: item.id,
        firstFilename: item.filename,
        firstTakenAt: item.metadata?.takenAt ?? null,
        firstContentType: item.contentType,
      });
    }
    const runCounts = new Map<string, number>();
    for (const run of consecutiveRuns) {
      runCounts.set(run.dayKey, (runCounts.get(run.dayKey) ?? 0) + 1);
    }
    const splitDays = [...runCounts.entries()]
      .filter(([, count]) => count > 1)
      .map(([dayKey, runs]) => ({ dayKey, runs }));

    const visible: Array<{
      key: string;
      dayKey: string;
      label: string;
      count: number;
      items: Array<GallerySlot & { data: Media }>;
    }> = [];

    for (const section of sections) {
      const items = section.items.flatMap((item) => {
        const index = indexById.get(item.id);
        if (index === undefined || index >= mountedCount) {
          return [];
        }
        return [
          {
            id: item.id,
            index,
            isLoaded: true as const,
            data: item,
          },
        ];
      });
      if (items.length === 0) {
        break;
      }
      visible.push({
        key: section.key,
        dayKey: section.dayKey,
        label: section.label,
        count: section.items.length,
        items,
      });
    }

    albumDebugDuration("gallery.daySections", startedAt, {
      albumId,
      sortBy,
      media: media.length,
      mountedCount,
      uniqueDays: sections.length,
      consecutiveRuns: consecutiveRuns.length,
      splitDays,
      visibleHeaders: visible.map((section) => ({
        dayKey: section.dayKey,
        label: section.label,
        count: section.count,
        mounted: section.items.length,
      })),
      firstRuns: consecutiveRuns.slice(0, 12),
    });

    return visible;
  }, [albumId, groupByDay, media, mountedCount, sortBy]);

  useEffect(() => {
    setCollapsedDays(new Set());
  }, [sortBy, albumId]);

  useEffect(() => {
    const startedAt = performance.now();
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        albumDebugDuration("gallery.painted", startedAt, {
          albumId,
          sortBy,
          media: media.length,
          mountedCount,
          visibleSlots: visibleSlots.length,
          dayHeaders: daySections.length,
          collapsedDays: collapsedDays.size,
          hasNextPage,
          isFetchingNextPage,
        });
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    albumId,
    collapsedDays.size,
    daySections.length,
    hasNextPage,
    isFetchingNextPage,
    media.length,
    mountedCount,
    sortBy,
    visibleSlots.length,
  ]);

  const toggleDaySection = useCallback((dayKey: string) => {
    setCollapsedDays((current) => {
      const next = new Set(current);
      if (next.has(dayKey)) {
        next.delete(dayKey);
      } else {
        next.add(dayKey);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || !fetchNextPage) {
      return;
    }

    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      {
        // Start the request as the 4th skeleton enters, not after the whole
        // placeholder block has been scrolled through.
        rootMargin: "200px",
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, mountedCount, daySections.length, prefetchSentinelId]);

  const openViewer = useCallback(
    (mediaItem: Media) => {
      setSelectedMedia(mediaItem);
      setIsImageViewerOpen(true);
      onImageViewerStateChange?.(true);
      onMediaClick?.(mediaItem);
    },
    [onImageViewerStateChange, onMediaClick]
  );

  const handleMediaClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, mediaItem: Media) => {
      if (Date.now() < suppressSelectionClickUntilRef.current) {
        suppressSelectionClickUntilRef.current = 0;
        return;
      }

      if (selectionMode) {
        onToggleSelection?.(mediaItem.id, { rangeSelect: event.shiftKey });
        return;
      }

      openViewer(mediaItem);
    },
    [onToggleSelection, openViewer, selectionMode]
  );

  const handleImageViewerClose = useCallback(() => {
    setIsImageViewerOpen(false);
    onImageViewerStateChange?.(false);
  }, [onImageViewerStateChange]);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }, []);

  const clearTouchSelectionTimer = useCallback(() => {
    const activeSelection = touchSelectionRef.current;

    if (!activeSelection?.longPressTimerId) return;

    window.clearTimeout(activeSelection.longPressTimerId);
    activeSelection.longPressTimerId = null;
  }, []);

  const endTouchSelection = useCallback(() => {
    clearTouchSelectionTimer();
    stopAutoScroll();
    touchSelectionRef.current = null;
    latestTouchPointRef.current = null;
    setIsTouchSelectionActive(false);
  }, [clearTouchSelectionTimer, stopAutoScroll]);

  useEffect(() => {
    return () => {
      endTouchSelection();
    };
  }, [endTouchSelection]);

  useEffect(() => {
    if (!isTouchSelectionActive) return;

    const preventDocumentScroll = (event: TouchEvent) => {
      if (event.touches.length === 1) {
        event.preventDefault();
      }
    };

    document.addEventListener("touchmove", preventDocumentScroll, { passive: false });

    return () => {
      document.removeEventListener("touchmove", preventDocumentScroll);
    };
  }, [isTouchSelectionActive]);

  const applyTouchSelectionRange = useCallback(
    (currentMediaId: string) => {
      const activeSelection = touchSelectionRef.current;

      if (!activeSelection?.isActive) return;
      if (activeSelection.currentMediaId === currentMediaId) return;

      activeSelection.currentMediaId = currentMediaId;
      onApplySelectionRange?.(
        activeSelection.anchorMediaId,
        currentMediaId,
        activeSelection.selected,
        activeSelection.baseSelectedMediaIds
      );
    },
    [onApplySelectionRange]
  );

  const applyTouchSelectionRangeFromPoint = useCallback(
    (x: number, y: number) => {
      const hoveredCard = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-media-id]");
      const hoveredMediaId = hoveredCard?.dataset.mediaId;

      if (hoveredMediaId) {
        applyTouchSelectionRange(hoveredMediaId);
      }
    },
    [applyTouchSelectionRange]
  );

  const stepAutoScroll = useCallback(() => {
    const point = latestTouchPointRef.current;
    const activeSelection = touchSelectionRef.current;

    if (!point || !activeSelection?.isActive) {
      stopAutoScroll();
      return;
    }

    const edgeThreshold = 96;
    const maxScrollPerFrame = 10;
    let scrollDelta = 0;

    if (point.y < edgeThreshold) {
      scrollDelta = -maxScrollPerFrame * ((edgeThreshold - point.y) / edgeThreshold);
    } else if (point.y > window.innerHeight - edgeThreshold) {
      scrollDelta = maxScrollPerFrame * ((point.y - (window.innerHeight - edgeThreshold)) / edgeThreshold);
    }

    if (scrollDelta !== 0) {
      window.scrollBy({ top: scrollDelta, behavior: "auto" });
      applyTouchSelectionRangeFromPoint(point.x, point.y);
    }

    autoScrollFrameRef.current = window.requestAnimationFrame(stepAutoScroll);
  }, [applyTouchSelectionRangeFromPoint, stopAutoScroll]);

  const ensureAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current !== null) return;
    autoScrollFrameRef.current = window.requestAnimationFrame(stepAutoScroll);
  }, [stepAutoScroll]);

  const activateTouchSelection = useCallback(() => {
    const activeSelection = touchSelectionRef.current;

    if (!activeSelection || activeSelection.isActive) return;

    activeSelection.isActive = true;
    activeSelection.longPressTimerId = null;
    suppressSelectionClickUntilRef.current = Date.now() + 1000;
    setIsTouchSelectionActive(true);
    onSelectionModeChange?.(true);
    onApplySelectionRange?.(
      activeSelection.anchorMediaId,
      activeSelection.anchorMediaId,
      activeSelection.selected,
      activeSelection.baseSelectedMediaIds
    );
    ensureAutoScroll();
  }, [ensureAutoScroll, onApplySelectionRange, onSelectionModeChange]);

  const handleSelectionTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>, mediaId: string, isSelected: boolean) => {
      const touch = event.touches[0];
      if (!touch) return;

      endTouchSelection();
      latestTouchPointRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };

      touchSelectionRef.current = {
        anchorMediaId: mediaId,
        currentMediaId: mediaId,
        selected: !isSelected,
        baseSelectedMediaIds: new Set(selectedMediaIds ?? []),
        isActive: false,
        longPressTimerId: window.setTimeout(() => {
          activateTouchSelection();
        }, 1000),
        startX: touch.clientX,
        startY: touch.clientY,
      };
    },
    [activateTouchSelection, endTouchSelection, selectedMediaIds]
  );

  const handleSelectionTouchMove = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const activeSelection = touchSelectionRef.current;
      const touch = event.touches[0];

      if (!activeSelection || !touch) return;

      latestTouchPointRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };

      if (!activeSelection.isActive) {
        const travelX = Math.abs(touch.clientX - activeSelection.startX);
        const travelY = Math.abs(touch.clientY - activeSelection.startY);

        if (travelX > 12 || travelY > 12) {
          clearTouchSelectionTimer();
          touchSelectionRef.current = null;
        }
        return;
      }

      applyTouchSelectionRangeFromPoint(touch.clientX, touch.clientY);
      ensureAutoScroll();

      event.preventDefault();
    },
    [applyTouchSelectionRangeFromPoint, clearTouchSelectionTimer, ensureAutoScroll]
  );

  const handleSelectionTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (touchSelectionRef.current?.isActive) {
        event.preventDefault();
      }

      endTouchSelection();
    },
    [endTouchSelection]
  );

  const renderGallerySlot = (item: GallerySlot, slotRef?: React.Ref<HTMLDivElement>) => {
    const mediaItem = item.data;
    const isVideo = mediaItem?.contentType?.startsWith("video/");
    const isSelected = Boolean(selectionMode && mediaItem && selectedMediaIds?.has(mediaItem.id));
    return (
      <div
        ref={slotRef}
        data-section="media-gallery-item"
        key={item.id}
        data-index={item.index}
        data-media-id={mediaItem?.id}
        className={["group relative aspect-square cursor-pointer", selectionMode ? "select-none" : ""].join(" ")}
        style={{
          animationDelay: item.index < 12 ? `${item.index * 40}ms` : "0ms",
          animation: item.isLoaded && item.index < 12 ? "fadeInUp 0.6s ease-out forwards" : "none",
          touchAction: isTouchSelectionActive ? "none" : "manipulation",
        }}
        onClick={(event) => mediaItem && handleMediaClick(event, mediaItem)}
        onTouchStart={(event) => mediaItem && handleSelectionTouchStart(event, mediaItem.id, isSelected)}
        onTouchMove={handleSelectionTouchMove}
        onTouchEnd={handleSelectionTouchEnd}
        onTouchCancel={handleSelectionTouchEnd}
      >
        {/* Image Container */}
        <div
          className={[
            "relative w-full h-full bg-white/10 backdrop-blur-xl rounded-2xl overflow-hidden border shadow-xl transition-all duration-500",
            selectionMode
              ? isSelected
                ? "border-purple-400/60 shadow-purple-500/30 ring-2 ring-purple-400/40"
                : "border-white/20"
              : "border-white/20 group-hover:shadow-purple-500/30 group-hover:-translate-y-1 group-hover:scale-105",
          ].join(" ")}
        >
          {/* Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10 blur-xl"></div>

          {/* Image or Placeholder */}
          {mediaItem?.url ? (
            <AuthenticatedImage
              src={mediaItem.url}
              alt={mediaItem.filename}
              className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
              loading="lazy"
            />
          ) : mediaItem ? (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-600 to-gray-700">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          ) : (
            <div className="w-full h-full bg-gray-700/50 animate-pulse flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}

          {isVideo && (
            <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 text-white text-xs font-semibold rounded-full flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4l12 6-12 6V4z" />
              </svg>
              Video
            </div>
          )}

          {selectionMode && mediaItem && (
            <div className="absolute top-3 right-3">
              <div
                className={[
                  "w-7 h-7 rounded-full border flex items-center justify-center backdrop-blur-xl",
                  isSelected ? "bg-purple-600/80 border-purple-300/60" : "bg-black/40 border-white/30",
                ].join(" ")}
                aria-label={isSelected ? "Selected" : "Not selected"}
              >
                {isSelected ? (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : null}
              </div>
            </div>
          )}

          {mediaItem && (
            <>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                <div className="flex gap-2"></div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                <p className="text-white text-xs font-medium truncate mb-1">{mediaItem.filename}</p>
                <p className="text-gray-300 text-xs">
                  {formatDistanceToNow(new Date(mediaItem.createdAt), { addSuffix: true })}
                </p>
              </div>

              {isDeleting && selectedMedia?.id === mediaItem.id && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // const handleDeleteClick = useCallback(
  //   (e: React.MouseEvent, mediaItem: Media) => {
  //     e.stopPropagation();
  //     onDeleteMedia?.(mediaItem);
  //   },
  //   [onDeleteMedia]
  // );

  if (isLoading) {
    return (
      <div data-section="media-gallery-root" className="space-y-6">
        {/* Loading Header */}
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gray-600/20 rounded w-32 animate-pulse" />
          <div className="h-10 bg-gray-600/20 rounded-xl w-24 animate-pulse" />
        </div>

        {/* Loading Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[...Array(12)].map((_, index) => (
            <div
              key={index}
              className="aspect-square bg-white/10 rounded-2xl animate-pulse"
              style={{ animationDelay: `${index * 100}ms` }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (media.length === 0) {
    return (
      <div
        data-section="media-gallery-root"
        className="w-full min-h-[50vh] flex items-center justify-center text-center py-20 px-4"
      >
        <div className="max-w-md mx-auto">
          {/* Empty State Icon */}
          <div className="relative mb-8">
            <div className="w-32 h-32 mx-auto jp-gradient-brand-soft rounded-full flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 rounded-full animate-pulse bg-[radial-gradient(circle,rgba(var(--primary-green-rgb),0.22),rgba(var(--primary-blue-rgb),0.16))]"></div>
              <svg
                className="w-16 h-16 text-gray-400 relative z-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-8 left-1/3 w-2 h-2 rounded-full animate-ping opacity-60 bg-[rgb(var(--primary-green-rgb))]"></div>
            <div
              className="absolute bottom-8 right-1/3 w-3 h-3 bg-blue-400 rounded-full animate-ping opacity-40"
              style={{ animationDelay: "1s" }}
            ></div>
          </div>

          <h3 className="text-2xl font-bold text-white mb-4">
            {isFiltered ? (
              <>
                Media (0){" - "}
                <button
                  type="button"
                  onClick={onFilteredClick}
                  className="text-[rgb(var(--primary-green-rgb))] underline decoration-[rgba(var(--primary-green-rgb),0.7)] underline-offset-4 hover:text-white"
                >
                  filtered
                </button>
              </>
            ) : (
              "No Media Yet"
            )}
          </h3>
          <p className="text-gray-300 mb-8 leading-relaxed">
            {isFiltered
              ? "No photos or videos match the current uploader filter."
              : "This album is waiting for your first memories. Upload photos or videos to bring it to life!"}
          </p>

          {showUploadButton && onUploadClick && (
            <button
              onClick={onUploadClick}
              className="jp-btn jp-btn--primary jp-btn--lg group inline-flex items-center gap-3"
            >
              <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center group-hover:rotate-180 transition-transform duration-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <span>Upload Media</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-section="media-gallery-root" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-xl font-bold text-white">
          Media ({totalMediaCount})
          {isFiltered ? (
            <>
              {" - "}
              <button
                type="button"
                onClick={onFilteredClick}
                className="text-[rgb(var(--primary-green-rgb))] underline decoration-[rgba(var(--primary-green-rgb),0.7)] underline-offset-4 hover:text-white"
              >
                filtered
              </button>
            </>
          ) : null}
        </h3>
        {showUploadButton && onUploadClick && (
          <button
            onClick={onUploadClick}
            className="group inline-flex w-fit items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl hover:bg-white/20 hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-300/50"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <span>Add Media</span>
          </button>
        )}
      </div>

      {groupByDay ? (
        <div className="space-y-4">
          {daySections.map((section) => {
            const collapsed = collapsedDays.has(section.dayKey);
            return (
              <section key={section.key} data-section="media-day-section" className="space-y-2">
                <MediaDaySectionHeader
                  label={section.label}
                  count={section.count}
                  collapsed={collapsed}
                  onToggle={() => toggleDaySection(section.dayKey)}
                />
                {!collapsed ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {section.items.map((item) => renderGallerySlot(item))}
                  </div>
                ) : null}
              </section>
            );
          })}
          {pendingSlots.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {pendingSlots.map((item, offset) =>
                renderGallerySlot(item, offset === prefetchSentinelIndex ? loadMoreSentinelRef : undefined)
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {visibleSlots.map((item) =>
            renderGallerySlot(item, item.id === prefetchSentinelId ? loadMoreSentinelRef : undefined)
          )}
        </div>
      )}

      {prefetchSentinelId ? null : <div ref={loadMoreSentinelRef} aria-hidden="true" className="h-px w-full" />}

      {/* Loading indicator */}
      {isFetchingNextPage && (
        <div className="flex justify-center py-8">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <p className="text-gray-300 text-sm">Loading more media...</p>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {selectedMedia && (
        <ImageViewer
          media={media}
          totalMediaCount={totalMediaCount}
          currentMediaId={selectedMedia.id}
          isOpen={isImageViewerOpen}
          onClose={handleImageViewerClose}
          ownerId={ownerId}
          albumId={albumId}
        />
      )}
    </div>
  );
};

export default MediaGallery;
